import {
  Button,
  Grid,
  Group,
  Select,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import React from "react";
import { z } from "zod";
import { Check, ExclamationMark } from "tabler-icons-react";

import { showNotification } from "@mantine/notifications";
import { useUser } from "hooks/useUser";
import { useGetOrg } from "api/organizations/getOrg";
import { useCreateClient } from "api/client/useCreateClient";

type AddClientFormFormProps = {
  onClose: () => void;
};

const addOrgMemberSchema = z.object({
  name: z.string().trim().min(1, { message: "Please enter your name" }),
  email: z
    .string()
    .email({ message: "Invalid email address" })
    .min(1, { message: "Please enter your email address" }),
  phone: z
    .string()
    .min(10, { message: "Phone # must contain at least 10 characters" }),
});

function AddClientForm({ onClose }: AddClientFormFormProps) {
  const { user } = useUser();
  const { data: org } = useGetOrg(user.org?._id);
  const { registerClient } = useCreateClient();

  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      notes: "",
      managedBy: "",
      description: "",
    },
    validate: zodResolver(addOrgMemberSchema),
  });

  type FormValues = typeof form.values;

  const userSelectOptions = org?.members.map((u) => ({
    value: u._id,
    label: `${u.firstName} ${u.lastName}`,
  }));

  const handleSubmit = (values: FormValues) => {
    registerClient(values, {
      onSuccess: () => {
        showNotification({
          title: "Success",
          message: `Client registered`,
          color: "teal",
          icon: <Check />,
        });
        onClose();
      },
      onError: () => {
        showNotification({
          title: "Server Error",
          message: "Please try again later",
          color: "red",
          icon: <ExclamationMark />,
        });
      },
    });
  };
  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label="Name"
            withAsterisk
            {...form.getInputProps("name")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Email"
            withAsterisk
            inputWrapperOrder={["label", "input", "description", "error"]}
            {...form.getInputProps("email")}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label="Phone #"
            withAsterisk
            {...form.getInputProps("phone")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label="Address" {...form.getInputProps("address")} />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={6}>
          <Select
            label="Managed By"
            data={userSelectOptions ?? []}
            size="md"
            {...form.getInputProps("managedBy")}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={12}>
          <Textarea
            label="Description"
            placeholder="Add any extra detail..."
            style={{ flex: 1 }}
            {...form.getInputProps("description")}
            minRows={2}
            autosize
          />
        </Grid.Col>
      </Grid>

      <Group position="right" spacing={20} mt={10}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="filled" disabled={!form.isDirty()}>
          Add
        </Button>
      </Group>
    </form>
  );
}

export default AddClientForm;
